import React, { useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CloseCallback, DrawerContextType } from './drawer-context';
import { Drawer, DrawerComponentProps } from '@/components/drawer';
import {
  DRAWER_WIDTH,
  OrderDrawerContext,
  OrderDrawerExtendedState,
  useOrderDrawerContext,
} from './order-drawer-context';
import { OrderDrawerManager } from '@/components/order-drawer/order-drawer-manager';
import { StyleSheet, View } from 'react-native';
import { useNewOrderActionsContext } from './new-order-actions-context';
import { useOrderInboundsStore } from '@/stores/order-inbounds-store';

const InboundOrderDrawerComponent: React.FC<
  DrawerComponentProps<OrderDrawerExtendedState>
> = ({ onClose, open, extendedState }) => {
  const { setCloseable, setCloseCallback, setDrawerState } =
    useOrderDrawerContext();

  return open ? (
    <OrderDrawerManager
      closeDrawer={onClose}
      onSubmitOrder={() => {
        setDrawerState({
          initialQuantityMap: undefined,
          smartOrderUUID: undefined,
        });
      }}
      onSubmitSmartOrder={extendedState?.onSubmitSmartOrder}
      setCloseable={setCloseable}
      setCloseCallback={setCloseCallback}
      smartOrderUUID={extendedState?.smartOrderUUID}
    />
  ) : (
    <View style={styles.centered}>
      <ActivityIndicator />
    </View>
  );
};

interface Props {
  children: React.ReactNode;
}

// Custom provider specific to smart order inbounds. Resets quantities in draft
// order upon closing the drawer, unless the order has been submitted.
export const OrderInboundDrawerContextProvider: React.FC<Props> = ({
  children,
}) => {
  const [closeable, setCloseable] = useState(true);
  const [closeCallback, setCloseCallback] = useState<CloseCallback | null>(
    null,
  );
  const [drawerState, setDrawerState] = useState<OrderDrawerExtendedState>();
  const { replaceOrderQuantities } = useNewOrderActionsContext();
  const clearPendingSmartOrders = useOrderInboundsStore(
    (state) => state.actions.clearPendingSmartOrders,
  );
  const takePendingReviewTiming = useOrderInboundsStore(
    (state) => state.actions.takePendingReviewTiming,
  );

  const [open, setOpen] = useState(false);
  const contextValue = useMemo<
    DrawerContextType<OrderDrawerExtendedState>
  >(() => {
    return {
      closeDrawer: () => {
        if (!closeable) {
          return;
        }

        setOpen(false);
        clearPendingSmartOrders();
        if (drawerState?.smartOrderUUID) {
          takePendingReviewTiming(drawerState.smartOrderUUID);
        }

        const quantityMap = drawerState?.initialQuantityMap;

        if (quantityMap) {
          replaceOrderQuantities(quantityMap, 'order_inbound_drawer');
        }

        // Call and reset the close callback
        closeCallback?.();
        setCloseCallback(null);
        setDrawerState(undefined);
      },
      open,
      openDrawer: () => setOpen(true),
      setCloseable,
      setCloseCallback: setCloseCallback,
      setDrawerState,
    };
  }, [
    drawerState?.initialQuantityMap,
    drawerState?.smartOrderUUID,
    clearPendingSmartOrders,
    takePendingReviewTiming,
    closeCallback,
    replaceOrderQuantities,
    closeable,
    open,
  ]);

  return (
    <OrderDrawerContext.Provider value={contextValue}>
      <Drawer<OrderDrawerExtendedState>
        ContentComponent={InboundOrderDrawerComponent}
        extendedState={drawerState}
        onClose={contextValue.closeDrawer}
        open={open}
        position="right"
        width={DRAWER_WIDTH}
      >
        {children}
      </Drawer>
    </OrderDrawerContext.Provider>
  );
};

const styles = StyleSheet.create({
  centered: {
    flex: 1,
    justifyContent: 'center',
  },
});
